/**
 * Dashboard Component - Survey Management Hub
 *
 * Central hub for managing surveys: create, view, and access analytics.
 * Field agents see only their assigned surveys with options to take surveys.
 */

import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import './App.css'
import './Dashboard.css'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import * as fieldAgentsApi from './api/fieldAgents'

const StatIcon = ({ type }) => {
  const iconMap = {
    total: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    published: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    draft: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    responses: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    active: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  }
  return iconMap[type] || iconMap.total
}

const ActionIcon = ({ type }) => {
  const iconMap = {
    edit: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    settings: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    share: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    analytics: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    preview: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    take: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  }
  return iconMap[type] || null
}

function Dashboard() {
  const { user, loading: authLoading, currentOrg, userRole } = useAuth()
  const [searchParams] = useSearchParams()
  const isWelcome = searchParams.get('welcome') === 'true'
  const [surveys, setSurveys] = useState([])
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const isFieldAgent = userRole === 'field_agent' || userRole === 'viewer'
  const canCreateSurvey = ['designer', 'survey_designer', 'administrator', 'owner'].includes(
    userRole
  )

  useEffect(() => {
    if (authLoading || !user) return

    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        if (isFieldAgent) {
          const result = await fieldAgentsApi.getMyAssignments()
          if (cancelled) return
          setAssignments(result || [])
        } else {
          const result = await surveysApi.listSurveys({
            organization_id: currentOrg?.id,
            limit: 50,
          })
          if (cancelled) return
          setSurveys(result.items || [])
        }
      } catch (e) {
        if (!cancelled) {
          setError(isFieldAgent ? 'Failed to load assignments' : 'Failed to load surveys')
          console.error('Failed to load data:', e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user, authLoading, isFieldAgent, currentOrg])

  const surveyStats = {
    total: surveys.length,
    active: surveys.filter((s) => s.status === 'published').length,
    draft: surveys.filter((s) => s.status === 'draft').length,
    totalResponses: surveys.reduce((sum, s) => sum + (s.response_count || 0), 0),
  }

  const assignmentStats = {
    total: assignments.length,
    active: assignments.filter((a) => {
      if (!a.survey_status || a.survey_status !== 'published') return false
      const now = new Date()
      const start = a.survey_scheduled_publish_at ? new Date(a.survey_scheduled_publish_at) : null
      const end = a.survey_scheduled_close_at ? new Date(a.survey_scheduled_close_at) : null
      if (start && now < start) return false
      if (end && now > end) return false
      return true
    }).length,
    completed: assignments.reduce((sum, a) => sum + (a.responses_count || 0), 0),
  }

  const isAssignmentActive = (assignment) => {
    if (!assignment.is_active) return false
    if (!assignment.survey_status || assignment.survey_status !== 'published') return false
    const now = new Date()
    const start = assignment.survey_scheduled_publish_at
      ? new Date(assignment.survey_scheduled_publish_at)
      : null
    const end = assignment.survey_scheduled_close_at
      ? new Date(assignment.survey_scheduled_close_at)
      : null
    if (start && now < start) return false
    if (end && now > end) return false
    return true
  }

  const getAssignmentStatus = (assignment) => {
    if (!assignment.is_active) {
      return { status: 'inactive', label: 'Inactive', className: 'badge-inactive' }
    }
    if (!assignment.survey_status || assignment.survey_status !== 'published') {
      return { status: 'draft', label: 'Not Published', className: 'badge-draft' }
    }
    const now = new Date()
    const start = assignment.survey_scheduled_publish_at
      ? new Date(assignment.survey_scheduled_publish_at)
      : null
    const end = assignment.survey_scheduled_close_at
      ? new Date(assignment.survey_scheduled_close_at)
      : null
    if (start && now < start) {
      return { status: 'scheduled', label: 'Scheduled', className: 'badge-scheduled' }
    }
    if (end && now > end) {
      return { status: 'ended', label: 'Ended', className: 'badge-ended' }
    }
    return { status: 'active', label: 'Active', className: 'badge-active' }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getSurveyStatus = (survey) => {
    const now = new Date()
    const startDate = survey.scheduled_publish_at ? new Date(survey.scheduled_publish_at) : null
    const endDate = survey.scheduled_close_at ? new Date(survey.scheduled_close_at) : null

    if (survey.status === 'draft') {
      return { status: 'draft', label: 'Draft', className: 'badge-draft' }
    }
    if (survey.status === 'archived') {
      return { status: 'archived', label: 'Archived', className: 'badge-inactive' }
    }

    if (survey.status === 'published') {
      const isWithinSchedule = startDate && endDate && now >= startDate && now <= endDate
      const isCurrentlyActive = survey.is_active || isWithinSchedule

      if (!survey.is_active && startDate && now < startDate) {
        return { status: 'scheduled', label: 'Scheduled', className: 'badge-scheduled' }
      }
      if (endDate && now > endDate && !survey.is_active) {
        return { status: 'ended', label: 'Ended', className: 'badge-ended' }
      }
      if (isCurrentlyActive) {
        return { status: 'active', label: 'Active', className: 'badge-active' }
      }
      return { status: 'inactive', label: 'Inactive', className: 'badge-inactive' }
    }

    return { status: survey.status, label: survey.status, className: 'badge-inactive' }
  }

  const formatSchedule = (survey) => {
    if (!survey.scheduled_publish_at && !survey.scheduled_close_at) return null
    const fmt = (dateStr) => {
      if (!dateStr) return null
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
    const start = fmt(survey.scheduled_publish_at)
    const end = fmt(survey.scheduled_close_at)
    if (start && end) return `${start} – ${end}`
    if (start) return `From ${start}`
    if (end) return `Until ${end}`
    return null
  }

  return (
    <div className="dashboard-page">
      {isWelcome && currentOrg && (
        <div className="welcome-banner">
          <div className="welcome-content">
            <div className="welcome-icon-wrap">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div>
              <h2>Welcome to {currentOrg.name}!</h2>
              <p>Your organization is ready. Start by creating your first survey.</p>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-container">
        {authLoading ? (
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p>Loading your dashboard...</p>
          </div>
        ) : !user ? (
          <div className="empty-state">
            <div className="empty-icon-wrap">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2>Authentication Required</h2>
            <p>Please log in to view and manage your surveys</p>
            <Link to="/login" className="btn btn-primary">
              Login to Continue
            </Link>
          </div>
        ) : (
          <>
            {/* Dashboard Header */}
            <div className="dashboard-header">
              <div>
                <h1>{isFieldAgent ? 'My Assignments' : 'Overview'}</h1>
                <p className="header-subtitle">
                  {isFieldAgent
                    ? 'View your assigned surveys and collect responses'
                    : 'Your aggregated survey performance at a glance'}
                </p>
              </div>
              {canCreateSurvey && (
                <Link to="/surveybuilder" className="btn-create">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Create Survey
                </Link>
              )}
            </div>

            {/* Stats Cards */}
            {isFieldAgent ? (
              <div className="stats-grid stats-grid--3col">
                <div className="stat-card">
                  <div className="stat-icon-corner">
                    <StatIcon type="total" />
                  </div>
                  <span className="stat-label">Assigned Surveys</span>
                  <span className="stat-value">{assignmentStats.total}</span>
                </div>
                <div className="stat-card stat-card--active">
                  <div className="stat-icon-corner">
                    <StatIcon type="active" />
                  </div>
                  <span className="stat-label">Active Now</span>
                  <span className="stat-value">{assignmentStats.active}</span>
                </div>
                <div className="stat-card stat-card--responses">
                  <div className="stat-icon-corner">
                    <StatIcon type="responses" />
                  </div>
                  <span className="stat-label">Responses Collected</span>
                  <span className="stat-value">{assignmentStats.completed}</span>
                </div>
              </div>
            ) : (
              <div className="stats-grid stats-grid--4col">
                <div className="stat-card">
                  <div className="stat-icon-corner">
                    <StatIcon type="total" />
                  </div>
                  <span className="stat-label">Total Surveys</span>
                  <span className="stat-value">{surveyStats.total}</span>
                </div>
                <div className="stat-card stat-card--active">
                  <div className="stat-icon-corner">
                    <StatIcon type="published" />
                  </div>
                  <span className="stat-label">Active Surveys</span>
                  <span className="stat-value">{surveyStats.active}</span>
                </div>
                <div className="stat-card stat-card--responses">
                  <div className="stat-icon-corner">
                    <StatIcon type="responses" />
                  </div>
                  <span className="stat-label">Total Responses</span>
                  <span className="stat-value">{surveyStats.totalResponses}</span>
                </div>
                <div className="stat-card stat-card--draft">
                  <div className="stat-icon-corner">
                    <StatIcon type="draft" />
                  </div>
                  <span className="stat-label">Drafts</span>
                  <span className="stat-value">{surveyStats.draft}</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="error-banner">
                <div className="error-banner-content">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{error}</span>
                </div>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            )}

            {/* Surveys/Assignments Section */}
            <div className="surveys-section">
              <div className="section-header">
                <h2>{isFieldAgent ? 'Assigned Surveys' : 'Your Surveys'}</h2>
              </div>

              {loading ? (
                <div className="loading-surveys">
                  <div className="loading-spinner small">
                    <div className="spinner-ring"></div>
                  </div>
                  <p>{isFieldAgent ? 'Loading assignments...' : 'Loading surveys...'}</p>
                </div>
              ) : isFieldAgent ? (
                assignments.length === 0 ? (
                  <div className="empty-surveys">
                    <div className="empty-illustration-wrap">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <h3>No surveys assigned yet</h3>
                    <p>You'll see surveys here once an administrator assigns them to you.</p>
                  </div>
                ) : (
                  <div className="surveys-list">
                    {assignments.map((assignment) => {
                      const statusInfo = getAssignmentStatus(assignment)
                      const canTakeSurvey = isAssignmentActive(assignment)
                      return (
                        <div key={assignment.id} className="survey-card">
                          <div className="survey-main">
                            <div className="survey-info">
                              <h3 className="survey-title">
                                {assignment.survey_title || 'Untitled Survey'}
                              </h3>
                              <div className="survey-meta">
                                <span className={`status-badge ${statusInfo.className}`}>
                                  {statusInfo.label}
                                </span>
                                {assignment.assigned_location_name && (
                                  <>
                                    <span className="meta-separator">·</span>
                                    <span className="meta-location">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{ verticalAlign: '-1px', marginRight: '3px' }}>
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                      </svg>
                                      {assignment.assigned_location_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="survey-stats">
                              <div className="survey-stat">
                                <span className="stat-num">{assignment.responses_count || 0}</span>
                                <span className="stat-text">Collected</span>
                              </div>
                            </div>
                          </div>
                          <div className="survey-actions">
                            {canTakeSurvey ? (
                              <Link
                                to={`/run/${assignment.survey_id}?token=${assignment.unique_token}`}
                                className="action-btn action-take">
                                <ActionIcon type="take" /> Take Survey
                              </Link>
                            ) : (
                              <span className="action-btn action-disabled">
                                {statusInfo.status === 'scheduled'
                                  ? 'Not Started'
                                  : statusInfo.status === 'ended'
                                    ? 'Closed'
                                    : 'Unavailable'}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              ) : surveys.length === 0 ? (
                <div className="empty-surveys">
                  <div className="empty-illustration-wrap">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M18 20V10M12 20V4M6 20v-6" />
                    </svg>
                  </div>
                  <h3>No surveys yet</h3>
                  <p>Create your first survey to start collecting responses</p>
                  {canCreateSurvey && (
                    <Link
                      to="/surveybuilder"
                      className="btn-create"
                      style={{ marginTop: '0.5rem' }}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      Create Your First Survey
                    </Link>
                  )}
                </div>
              ) : (
                <div className="surveys-list">
                  {surveys.map((survey) => {
                    const schedule = formatSchedule(survey)
                    const statusInfo = getSurveyStatus(survey)
                    return (
                      <div key={survey.id} className="survey-card">
                        <div className="survey-main">
                          <div className="survey-info">
                            <h3 className="survey-title">
                              <Link
                                to={
                                  statusInfo.status === 'draft'
                                    ? `/survey/${survey.id}/edit`
                                    : `/survey/${survey.id}/settings`
                                }
                                style={{ color: 'inherit', textDecoration: 'none' }}
                                title={
                                  statusInfo.status === 'draft' ? 'Edit survey' : 'Survey settings'
                                }>
                                {survey.title || 'Untitled Survey'}
                              </Link>
                            </h3>
                            <div className="survey-meta">
                              <span className={`status-badge ${statusInfo.className}`}>
                                {statusInfo.label}
                              </span>
                              <span className="meta-separator">·</span>
                              <span className="meta-date">
                                Created {formatDate(survey.created_at)}
                              </span>
                              {survey.owner_name && (
                                <>
                                  <span className="meta-separator">·</span>
                                  <span className="meta-author" title={survey.owner_email || ''}>
                                    {survey.owner_name}
                                  </span>
                                </>
                              )}
                              {schedule && (
                                <>
                                  <span className="meta-separator">·</span>
                                  <span className="meta-schedule">{schedule}</span>
                                </>
                              )}
                              {survey.requires_field_agent && (
                                <>
                                  <span className="meta-separator">·</span>
                                  <span className="meta-field-agent">Field Agent Only</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="survey-stats">
                            <div className="survey-stat">
                              <span className="stat-num">{survey.response_count || 0}</span>
                              <span className="stat-text">Responses</span>
                            </div>
                          </div>
                        </div>
                        <div className="survey-actions">
                          {statusInfo.status === 'draft' && (
                            <Link
                              to={`/survey/${survey.id}/edit`}
                              className="action-btn action-edit"
                              title="Edit survey">
                              <ActionIcon type="edit" /> Edit
                            </Link>
                          )}
                          <Link
                            to={`/survey/${survey.id}/settings`}
                            className="action-btn action-settings"
                            title="Survey settings">
                            <ActionIcon type="settings" /> Settings
                          </Link>
                          {(statusInfo.status === 'active' ||
                            statusInfo.status === 'published') && (
                            <button
                              className="action-btn action-share"
                              onClick={() => {
                                const url =
                                  survey.short_url || `${window.location.origin}/run/${survey.id}`
                                const message = `Please take a moment to complete this survey:\n\n${survey.title || 'Survey'}\n\n${url}`
                                navigator.clipboard.writeText(message)
                                alert('Survey link copied to clipboard!')
                              }}
                              title="Copy share link">
                              <ActionIcon type="share" /> Share
                            </button>
                          )}
                          <Link
                            to={`/analytics/${survey.id}`}
                            className="action-btn action-analytics"
                            title="View analytics">
                            <ActionIcon type="analytics" /> Analytics
                          </Link>
                          <Link
                            to={`/run/${survey.id}`}
                            className="action-btn action-preview"
                            title="Preview survey"
                            target="_blank">
                            <ActionIcon type="preview" /> Preview
                          </Link>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Dashboard
